/* eslint-disable */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, ArrowUpRight, Hourglass, Sparkle, WarningCircle, EnvelopeSimple } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { HaleWordmark } from '../components/HaleBrand';
import TI from '../components/brand/TI';
import BrandWorkflowStatusCard from '../components/hale/BrandWorkflowStatusCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IVORY = '#F5F1E8';
const SLATE = '#0B1426';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const RULE  = 'rgba(11,20,38,0.10)';

/* /hale/dashboard — auth-gated summary tile for brands inside Hale.
 *
 * Renders a compact decision surface: application state, membership
 * state, SKU portfolio counts, and one clear "next action" button.
 * Visual system matches the approved ToxIntel landing aesthetic. */

const STATUS_TONE = {
  pending_review:                { label: 'Pending review',         color: '#A07A14', bg: 'rgba(212,160,23,0.10)' },
  pending_revision:              { label: 'Revisions requested',    color: '#A07A14', bg: 'rgba(212,160,23,0.10)' },
  approved:                      { label: 'Approved',               color: '#16624B', bg: 'rgba(22,98,75,0.10)' },
  approved_with_recommendations: { label: 'Approved (with notes)',  color: '#16624B', bg: 'rgba(22,98,75,0.10)' },
  not_approved:                  { label: 'Not approved',           color: '#B0312D', bg: 'rgba(176,49,45,0.10)' },
  active:                        { label: 'Active',                 color: '#16624B', bg: 'rgba(22,98,75,0.10)' },
};

function StatusBadge({ status, testid }) {
  if (!status) return null;
  const tone = STATUS_TONE[status] || { label: status, color: MUTED, bg: 'rgba(11,20,38,0.05)' };
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-sm text-[10px] font-mono uppercase tracking-[0.18em]"
      style={{ color: tone.color, backgroundColor: tone.bg }}
      data-testid={testid}
    >
      {tone.label}
    </span>
  );
}

export default function HaleDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const r = await axios.get(`${API_URL}/api/hale/dashboard`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate('/login?return=/hale/dashboard');
        return;
      }
      setError(e?.response?.data?.detail || 'Could not load your Hale dashboard right now.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login?return=/hale/dashboard');
      return;
    }
    fetchDashboard();
  }, [user, authLoading, fetchDashboard, navigate]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Hale Dashboard · ToxIntel</title>
      </Helmet>

      {/* Header — same lockup as the Hale landing */}
      <header className="border-b" style={{ borderColor: RULE }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3" aria-label="ToxIntel home">
              <span className="text-xl tracking-tight" style={{ color: SLATE, fontFamily: '"Cormorant Garamond", serif' }}>
                <TI color={CORAL} />
              </span>
            </Link>
            <span
              className="ml-3 text-[10px] font-mono uppercase tracking-[0.25em] px-2 py-1 rounded-sm"
              style={{ backgroundColor: SLATE, color: IVORY }}
            >
              · Hale dashboard
            </span>
          </div>
          <nav className="flex items-center gap-5">
            <Link to="/hale" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="hale-dash-nav-landing">
              Hale overview
            </Link>
            <Link to="/hale/membership" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="hale-dash-nav-membership">
              Membership
            </Link>
            <Link to="/dashboard" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: SLATE }} data-testid="hale-dash-nav-toxintel">
              <TI color={CORAL} />&nbsp;&rarr;
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
        <div className="mb-10">
          <HaleWordmark size="md" />
          <h1
            className="mt-5 text-3xl sm:text-4xl font-extrabold tracking-tight"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
            data-testid="hale-dashboard-title"
          >
            Your Hale dashboard.
          </h1>
          <p className="mt-3 text-sm" style={{ color: MUTED }}>
            Signed in as <span className="font-semibold" style={{ color: SLATE }}>{user?.email || '—'}</span>.
            Track your application, membership, and SKU portfolio in one place.
          </p>
        </div>

        {loading && (
          <div className="rounded-sm border p-8" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.5)' }}>
            <p className="text-sm font-mono uppercase tracking-[0.2em]" style={{ color: MUTED }} data-testid="hale-dash-loading">
              Loading dashboard…
            </p>
          </div>
        )}

        {error && !loading && (
          <div
            className="rounded-sm border p-7 flex items-start gap-3"
            style={{ borderColor: 'rgba(176,49,45,0.4)', backgroundColor: 'rgba(176,49,45,0.05)' }}
            data-testid="hale-dash-error"
          >
            <WarningCircle size={20} weight="fill" style={{ color: '#B0312D' }} className="mt-0.5" />
            <div>
              <p className="text-sm font-semibold" style={{ color: '#B0312D' }}>{error}</p>
              <p className="text-[12px] mt-1" style={{ color: MUTED }}>
                If this persists, contact <a href="mailto:governance@tagc.us" className="underline">governance@tagc.us</a>.
              </p>
            </div>
          </div>
        )}

        {data && !loading && (
          <>
            {/* New three-status workflow status card — brand-facing only */}
            <div className="mb-6">
              <BrandWorkflowStatusCard />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* APPLICATION */}
              <div
                className="rounded-sm border p-7"
                style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}
                data-testid="hale-dash-application"
              >
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3" style={{ color: CORAL }}>
                Application
              </p>
              {data.application ? (
                <>
                  <p className="text-2xl font-extrabold tracking-tight mb-2"
                     style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
                    {data.application.brand_name || 'Your application'}
                  </p>
                  <StatusBadge status={data.application.status} testid="hale-dash-app-status" />
                  <ul className="mt-4 space-y-2 text-[13px]" style={{ color: MUTED }}>
                    {data.application.track && (
                      <li><span className="font-mono uppercase text-[10px] tracking-wider">Track</span> · {data.application.track === 'hale_reviewed' ? 'Reviewed™' : data.application.track === 'hale_verified' ? 'Verified™' : 'Both'}</li>
                    )}
                    {data.application.tier && (
                      <li><span className="font-mono uppercase text-[10px] tracking-wider">Tier</span> · {data.application.tier.charAt(0).toUpperCase() + data.application.tier.slice(1)}</li>
                    )}
                    {data.application.submitted_at && (
                      <li><span className="font-mono uppercase text-[10px] tracking-wider">Submitted</span> · {new Date(data.application.submitted_at).toLocaleDateString()}</li>
                    )}
                  </ul>
                </>
              ) : (
                <>
                  <p className="text-base mb-3" style={{ color: SLATE }}>You haven&apos;t started a Hale application yet.</p>
                  <p className="text-sm" style={{ color: MUTED }}>The intake takes about 12 minutes and is auto-saved as you go.</p>
                </>
              )}
            </div>

            {/* MEMBERSHIP */}
            <div
              className="rounded-sm border p-7"
              style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}
              data-testid="hale-dash-membership"
            >
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3" style={{ color: CORAL }}>
                Membership
              </p>
              {data.membership ? (
                <>
                  <p className="text-2xl font-extrabold tracking-tight mb-2"
                     style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
                    {data.membership.track === 'hale_reviewed' ? 'Reviewed™' : 'Verified™'} · {data.membership.tier?.charAt(0).toUpperCase() + data.membership.tier?.slice(1)}
                  </p>
                  <StatusBadge status={data.membership.status} testid="hale-dash-member-status" />
                  <ul className="mt-4 space-y-2 text-[13px]" style={{ color: MUTED }}>
                    {data.membership.activated_at && (
                      <li><span className="font-mono uppercase text-[10px] tracking-wider">Activated</span> · {new Date(data.membership.activated_at).toLocaleDateString()}</li>
                    )}
                    {data.membership.next_renewal_at && (
                      <li><span className="font-mono uppercase text-[10px] tracking-wider">Next renewal</span> · {new Date(data.membership.next_renewal_at).toLocaleDateString()}</li>
                    )}
                    <li className="flex items-center gap-2">
                      <span className="font-mono uppercase text-[10px] tracking-wider">Badge</span> ·{' '}
                      {data.membership.badge_eligible
                        ? <><CheckCircle size={12} weight="fill" style={{ color: CORAL }} /> Eligible</>
                        : <><Hourglass size={12} weight="bold" style={{ color: MUTED }} /> Pending</>}
                    </li>
                  </ul>
                </>
              ) : (
                <>
                  <p className="text-base mb-3" style={{ color: SLATE }}>No active membership yet.</p>
                  <p className="text-sm" style={{ color: MUTED }}>Once your application is approved, your reviewer will activate the membership.</p>
                </>
              )}
            </div>

            {/* PORTFOLIO */}
            <div
              className="rounded-sm border p-7"
              style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}
              data-testid="hale-dash-portfolio"
            >
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3" style={{ color: CORAL }}>
                SKU portfolio
              </p>
              <p className="text-4xl font-extrabold tracking-tight tabular-nums mb-2"
                 style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
                {data.portfolio.total_skus}
              </p>
              <p className="text-sm" style={{ color: MUTED }}>SKUs in your Hale portfolio</p>
              <p className="mt-4 text-[13px] flex items-center gap-2" style={{ color: SLATE }}>
                <Sparkle size={14} weight="fill" style={{ color: CORAL }} />
                <span className="tabular-nums font-semibold">{data.portfolio.certified_skus}</span>
                <span style={{ color: MUTED }}>certified · badge-eligible</span>
              </p>
            </div>

            {/* NEXT ACTION (full-width tile below) */}
            <div
              className="lg:col-span-3 rounded-sm border-2 p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
              style={{ borderColor: CORAL, backgroundColor: IVORY }}
              data-testid="hale-dash-next-action"
            >
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-2" style={{ color: CORAL }}>
                  Recommended next action
                </p>
                <p className="text-xl font-extrabold tracking-tight" style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
                  {data.next_action.label}
                </p>
              </div>
              <Link
                to={data.next_action.href}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-sm text-[13px] font-medium uppercase tracking-wider transition-transform hover:-translate-y-px shadow-sm"
                style={{
                  backgroundColor: data.next_action.kind === 'primary' ? CORAL : SLATE,
                  color: data.next_action.kind === 'primary' ? SLATE : IVORY,
                }}
                data-testid="hale-dash-next-action-cta"
              >
                Take action <ArrowUpRight size={14} weight="bold" />
              </Link>
            </div>

            {/* REVIEWER CONTACT */}
            <div
              className="lg:col-span-3 rounded-sm border p-5 flex items-center gap-3"
              style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.4)' }}
              data-testid="hale-dash-reviewer"
            >
              <EnvelopeSimple size={18} style={{ color: CORAL }} />
              <p className="text-sm" style={{ color: SLATE }}>
                Reviewer contact ·{' '}
                <a href={`mailto:${data.reviewer_contact}`} className="underline" style={{ color: CORAL }}>
                  {data.reviewer_contact}
                </a>
              </p>
            </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
